import { React, useEffect } from "react";
import CustomInput from "../components/CustomInput";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  createGoldScheme,
  getAGoldScheme,
  getSchemeNames,
  resetState,
  updateAGoldScheme,
} from "../features/goldScheme/goldSchemeSlice";

const baseSchema = {
  full_name: yup.string().required("Full name is required"),
  email: yup.string().email("Enter a valid email").required("Email is required"),
  mobile: yup
    .string()
    .matches(/^[0-9]{10}$/, "Enter a valid 10-digit mobile number")
    .required("Mobile number is required"),
  scheme_name: yup.string().required("Scheme name is required"),
  hapta_paid: yup
    .number()
    .typeError("Installment paid must be a number")
    .min(0, "Amount cannot be negative")
    .required("Installment paid amount is required"),
};

const AddGoldScheme = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const getSchemeId = location.pathname.split("/")[3];
  const isEdit = getSchemeId !== undefined;

  const { isSuccess, isError, createdScheme, updatedScheme, schemeDetail, schemeNames } =
    useSelector((state) => state.goldScheme);

  const schema = yup.object().shape({
    ...baseSchema,
    password: isEdit
      ? yup.string().min(6, "Password must be at least 6 characters").optional()
      : yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
  });

  useEffect(() => {
    dispatch(getSchemeNames());
    if (isEdit) {
      dispatch(getAGoldScheme(getSchemeId));
    } else {
      dispatch(resetState());
    }
  }, [dispatch, getSchemeId, isEdit]);

  useEffect(() => {
    if (isSuccess && createdScheme) {
      toast.success("Scheme customer added successfully!");
      navigate("/admin/list-gold-scheme");
      dispatch(resetState());
    }
    if (isSuccess && updatedScheme) {
      toast.success("Scheme customer updated successfully!");
      navigate("/admin/list-gold-scheme");
      dispatch(resetState());
    }
    if (isError) {
      toast.error("Something went wrong!");
    }
  }, [isSuccess, isError, createdScheme, updatedScheme, navigate, dispatch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      full_name: schemeDetail?.full_name || "",
      email: schemeDetail?.email || "",
      mobile: schemeDetail?.mobile || "",
      password: "",
      scheme_name: schemeDetail?.scheme_name || "",
      hapta_paid: schemeDetail?.hapta_paid ?? "",
    },
    validationSchema: schema,
    onSubmit: (values) => {
      const payload = {
        full_name: values.full_name,
        email: values.email,
        mobile: values.mobile,
        scheme_name: values.scheme_name,
        hapta_paid: Number(values.hapta_paid),
      };

      if (values.password) {
        payload.password = values.password;
      }

      if (isEdit) {
        dispatch(updateAGoldScheme({ id: getSchemeId, schemeData: payload }));
      } else {
        dispatch(createGoldScheme(payload));
      }
    },
  });

  return (
    <div>
      <h3 className="mb-4 title">{isEdit ? "Edit" : "Add"} 11+1 / Gold Scheme Customer</h3>
      <form onSubmit={formik.handleSubmit}>
        <CustomInput
          type="text"
          name="full_name"
          onChng={formik.handleChange("full_name")}
          onBlr={formik.handleBlur("full_name")}
          val={formik.values.full_name}
          label="Customer Full Name"
          id="full_name"
        />
        <div className="error">
          {formik.touched.full_name && formik.errors.full_name}
        </div>

        <CustomInput
          type="email"
          name="email"
          onChng={formik.handleChange("email")}
          onBlr={formik.handleBlur("email")}
          val={formik.values.email}
          label="Email"
          id="email"
        />
        <div className="error">
          {formik.touched.email && formik.errors.email}
        </div>

        <CustomInput
          type="text"
          name="mobile"
          onChng={formik.handleChange("mobile")}
          onBlr={formik.handleBlur("mobile")}
          val={formik.values.mobile}
          label="Mobile Number"
          id="mobile"
        />
        <div className="error">
          {formik.touched.mobile && formik.errors.mobile}
        </div>

        <CustomInput
          type="password"
          name="password"
          onChng={formik.handleChange("password")}
          onBlr={formik.handleBlur("password")}
          val={formik.values.password}
          label={isEdit ? "Password (leave blank to keep current)" : "Password"}
          id="password"
        />
        <div className="error">
          {formik.touched.password && formik.errors.password}
        </div>

        <div className="mt-3 mb-2">
          <label htmlFor="scheme_name" className="form-label">
            Scheme Name
          </label>
          <select
            id="scheme_name"
            name="scheme_name"
            className="form-control"
            value={formik.values.scheme_name}
            onChange={formik.handleChange("scheme_name")}
            onBlur={formik.handleBlur("scheme_name")}
          >
            <option value="">Select scheme</option>
            {schemeNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          {schemeNames.length === 0 && (
            <small className="text-muted">
              No scheme names found. Add them in Settings first.
            </small>
          )}
        </div>
        <div className="error">
          {formik.touched.scheme_name && formik.errors.scheme_name}
        </div>

        <CustomInput
          type="number"
          name="hapta_paid"
          onChng={formik.handleChange("hapta_paid")}
          onBlr={formik.handleBlur("hapta_paid")}
          val={formik.values.hapta_paid}
          label="Installment Paid (₹)"
          id="hapta_paid"
        />
        <div className="error">
          {formik.touched.hapta_paid && formik.errors.hapta_paid}
        </div>

        <button
          className="btn btn-success border-0 rounded-3 my-5"
          type="submit"
        >
          {isEdit ? "Update" : "Add"} Scheme Customer
        </button>
      </form>
    </div>
  );
};

export default AddGoldScheme;
